// File: controllers/companySettingsController.js

import apiService from '../services/apiService';

class CompanySettingsController {

  // ══════════════════════════════════════════════════════
  // READ
  // ══════════════════════════════════════════════════════

  async getSettings() {
    try {
      const response = await apiService.get('company-settings');
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] GET /company-settings:', error.message);
      return { success: false, error: error.message };
    }
  }
// ══════════════════════════════════════════════════════
// COMPANY LIST
// ══════════════════════════════════════════════════════

async getAllCompanies({ page = 1, limit = 10, search = '', sortBy = 'createdAt', order = 'desc' } = {}) {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (search)  params.append('search', search);
    if (sortBy)  params.append('sortBy', sortBy);
    if (order)   params.append('order', order);

    const response = await apiService.get(`company-settings/all?${params.toString()}`);
    return {
      success:    true,
      data:       response.data       || [],
      pagination: response.pagination || { total: 0, page, limit, totalPages: 1 },
      message:    response.message,
    };
  } catch (error) {
    console.error('[CompanySettings] GET /company-settings/all:', error.message);
    return { success: false, data: [], error: error.message };
  }
}
  async getPublicSettings() {
    try {
      const response = await apiService.get('company-settings/public');
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] GET /company-settings/public:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // CREATE
  // ══════════════════════════════════════════════════════

  async initSettings(data) {
    try {
      const response = await apiService.post('company-settings/init', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] POST /company-settings/init:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // UPDATE — full
  // ══════════════════════════════════════════════════════

  async updateSettings(data) {
    try {
      const response = await apiService.put('company-settings', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // UPDATE — section-wise
  // ══════════════════════════════════════════════════════

  async updateGeneralInfo(data) {
    try {
      const response = await apiService.put('company-settings/general', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/general:', error.message);
      return { success: false, error: error.message };
    }
  }

  async updateContactInfo(data) {
    try {
      const response = await apiService.put('company-settings/contact', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/contact:', error.message);
      return { success: false, error: error.message };
    }
  }

  async updatePreferences(data) {
    try {
      const response = await apiService.put('company-settings/preferences', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/preferences:', error.message);
      return { success: false, error: error.message };
    }
  }

  async updateBranding(data, logoFile = null, faviconFile = null) {
    try {
      if (logoFile || faviconFile) {
        const formData = new FormData();
        if (logoFile)            formData.append('logo', logoFile);
        if (faviconFile)         formData.append('favicon', faviconFile);
        if (data.primaryColor)   formData.append('primaryColor', data.primaryColor);
        if (data.secondaryColor) formData.append('secondaryColor', data.secondaryColor);
        if (data.accentColor)    formData.append('accentColor', data.accentColor);

        const response = await apiService.put('company-settings/branding', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return { success: true, data: response.data, message: response.message };
      }

      // Sirf colors update karna ho
      const response = await apiService.put('company-settings/branding', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/branding:', error.message);
      return { success: false, error: error.message };
    }
  }

  async updateBusinessSettings(data) {
    try {
      const response = await apiService.put('company-settings/business', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/business:', error.message);
      return { success: false, error: error.message };
    }
  }

  async updateNotificationSettings(data) {
    try {
      const response = await apiService.put('company-settings/notifications', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/notifications:', error.message);
      return { success: false, error: error.message };
    }
  }

  async updateSecuritySettings(data) {
    try {
      const response = await apiService.put('company-settings/security', data);
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] PUT /company-settings/security:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // UPLOAD — alag alag
  // ══════════════════════════════════════════════════════

  async uploadLogo(file) {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await apiService.post('company-settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] POST /company-settings/upload-logo:', error.message);
      return { success: false, error: error.message };
    }
  }

  async uploadFavicon(file) {
    try {
      const formData = new FormData();
      formData.append('favicon', file);
      const response = await apiService.post('company-settings/upload-favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] POST /company-settings/upload-favicon:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // DELETE
  // ══════════════════════════════════════════════════════

  async deleteLogo() {
    try {
      const response = await apiService.delete('company-settings/logo');
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] DELETE /company-settings/logo:', error.message);
      return { success: false, error: error.message };
    }
  }

  async deleteFavicon() {
    try {
      const response = await apiService.delete('company-settings/favicon');
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] DELETE /company-settings/favicon:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // SECURITY — IP & Domain helpers
  // ══════════════════════════════════════════════════════

  async addIPToWhitelist(ip, currentList) {
    const ipWhitelist = [...new Set([...currentList, ip])];
    return this.updateSecuritySettings({ ipWhitelist });
  }

  async removeIPFromWhitelist(ip, currentList) {
    const ipWhitelist = currentList.filter(i => i !== ip);
    return this.updateSecuritySettings({ ipWhitelist });
  }

  async addAllowedDomain(domain, currentList) {
    const allowedDomains = [...new Set([...currentList, domain])];
    return this.updateSecuritySettings({ allowedDomains });
  }

  async removeAllowedDomain(domain, currentList) {
    const allowedDomains = currentList.filter(d => d !== domain);
    return this.updateSecuritySettings({ allowedDomains });
  }

  // ══════════════════════════════════════════════════════
  // RESET
  // ══════════════════════════════════════════════════════

  async resetToDefaults() {
    try {
      const response = await apiService.post('company-settings/reset');
      return { success: true, data: response.data, message: response.message };
    } catch (error) {
      console.error('[CompanySettings] POST /company-settings/reset:', error.message);
      return { success: false, error: error.message };
    }
  }

  // ══════════════════════════════════════════════════════
  // EXPORT / IMPORT (local utility)
  // ══════════════════════════════════════════════════════

  exportSettings(settings) {
    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'company-settings.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  async importSettings(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      return this.updateSettings(data);
    } catch {
      return { success: false, error: 'Invalid JSON file' };
    }
  }
}

export const companySettingsController = new CompanySettingsController();